import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import { useFormik } from 'formik';

import { getErrorMessage } from '@utils/validation';

import Modal from '@common/components/Modal';
import ConfirmationDialog from '@common/components/Modal/ConfirmationDialog';
import { FormControl, Textarea, TextInput } from '@common/components/forms';
import Button, { BUTTON_VARIANTS } from '@common/components/Button';
import { explicitNull, or } from '@common/tempAirbnbPropTypesReplacement';

import {
  PersonalizationModalWrapper,
  PersonalizationModalForm,
  PersonalizationModalInner,
  PersonalizationInputRow,
  PersonalizationInputHint,
  PersonalizationActionRow,
} from './PersonalizationModal.styled';

const preventNewLine = e => {
  if (e.keyCode === 13) {
    e.preventDefault();

    return false;
  }

  return true;
};

const initialValues = {
  to: '',
  message: '',
  from: '',
};

const PersonalizationModal = ({
  giftCardId,
  personalization,
  onClose,
  onDiscard,
}) => {
  const { t } = useTranslation();
  const personalizationModalWrapperRef = useRef(null);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);

  const {
    values,
    touched,
    errors,
    dirty,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      ...personalization,
    },
    onSubmit(submitValues, formikHelpers) {
      onClose(giftCardId, submitValues);
      formikHelpers.resetForm(initialValues);
    },
  });

  const commonProps = {
    onBlur: handleBlur,
    onChange: handleChange,
  };

  const handleDiscard = () => {
    if (dirty) {
      setIsDiscardConfirmOpen(true);
    } else {
      onDiscard();
    }
  };
  const confirmDiscardChanges = () => {
    onDiscard();
    resetForm(initialValues);
    setIsDiscardConfirmOpen(false);
  };
  const closeConfirmModal = () => setIsDiscardConfirmOpen(false);

  return (
    <Modal
      isOpen={!!giftCardId}
      fullScreen
      appear
      exit
      enter
      mountOnEnter
      unmountOnExit
      timeout={0}
      shouldRenderFocusTrap
      withCloseButton={false}
      onClose={handleDiscard}
    >
      <PersonalizationModalWrapper
        ref={personalizationModalWrapperRef}
        onClick={({ target }) =>
          target === personalizationModalWrapperRef.current && handleDiscard()
        }
      >
        <PersonalizationModalForm onSubmit={handleSubmit}>
          <PersonalizationModalInner>
            <PersonalizationInputRow>
              <FormControl
                hasError={touched?.to && !!errors?.to}
                message={touched?.to && getErrorMessage(t, errors?.to)}
                label={t(
                  'giftCards.order.dedicationModal.salutation.label',
                  'Ansprache',
                )}
                optional
                noSpacing
              >
                <TextInput
                  {...commonProps}
                  value={values.to}
                  hasError={touched?.to && !!errors?.to}
                  name="to"
                  placeholder={t(
                    'giftCards.order.dedicationModal.salutation.placeholder',
                    'AdressLiebe Doriszusatz',
                  )}
                  maxLength="50"
                />
              </FormControl>
              <PersonalizationInputHint>
                {t(
                  'giftCards.order.dedicationModal.characterCounter',
                  '{{used}} von {{available}} Zeichen',
                  { used: values.to.length, available: 50 },
                )}
              </PersonalizationInputHint>
            </PersonalizationInputRow>

            <PersonalizationInputRow>
              <FormControl
                hasError={touched?.to && !!errors?.to}
                message={touched?.to && getErrorMessage(t, errors?.to)}
                label={t(
                  'giftCards.order.dedicationModal.text.label',
                  'Widmung',
                )}
                optional
                noSpacing
              >
                <Textarea
                  {...commonProps}
                  name="message"
                  value={values.message}
                  placeholder={t(
                    'giftCards.order.dedicationModal.text.placeholder',
                    'Deine Nachricht',
                  )}
                  maxLength="300"
                  onKeyDown={preventNewLine}
                />
              </FormControl>
              <PersonalizationInputHint>
                {t(
                  'giftCards.order.dedicationModal.characterCounter',
                  '{{used}} von {{available}} Zeichen',
                  { used: values.message.length, available: 300 },
                )}
              </PersonalizationInputHint>
            </PersonalizationInputRow>

            <PersonalizationInputRow>
              <FormControl
                hasError={touched?.from && !!errors?.from}
                message={touched?.from && getErrorMessage(t, errors?.from)}
                label={t(
                  'giftCards.order.dedicationModal.sender.label',
                  'Abesender',
                )}
                optional
                noSpacing
              >
                <TextInput
                  {...commonProps}
                  value={values.from}
                  hasError={touched?.from && !!errors?.from}
                  name="from"
                  placeholder={t(
                    'giftCards.order.dedicationModal.sender.placeholder',
                    'Gruss von Doris',
                  )}
                  maxLength="50"
                />
              </FormControl>
              <PersonalizationInputHint>
                {t(
                  'giftCards.order.dedicationModal.characterCounter',
                  '{{used}} von {{available}} Zeichen',
                  { used: values.from.length, available: 50 },
                )}
              </PersonalizationInputHint>
            </PersonalizationInputRow>
          </PersonalizationModalInner>

          <PersonalizationActionRow>
            <Button
              type="submit"
              variant={BUTTON_VARIANTS.LEVEL_1_GREEN_SMALL}
            >
              {dirty
                ? t('giftCards.order.dedicationModal.buttonApply', 'Anwenden')
                : t(
                    'giftCards.order.dedicationModal.buttonCancel',
                    'Abbrechen',
                  )}
            </Button>
          </PersonalizationActionRow>
        </PersonalizationModalForm>
      </PersonalizationModalWrapper>

      <Modal
        isOpen={isDiscardConfirmOpen}
        onClose={closeConfirmModal}
        withCloseButton={false}
        mountOnEnter
        unmountOnExit
        appear
        exit
        enter
        timeout={500}
      >
        <ConfirmationDialog
          onDismiss={closeConfirmModal}
          onConfirm={confirmDiscardChanges}
          title={t(
            'confirmationDialog.unsavedData.title',
            'Änderungen verwerfen??',
          )}
          description={t(
            'confirmationDialog.unsavedData.description',
            'Möchtest Du Deine ungespeicherten Änderungen wirklich verwerfen?',
          )}
          dismissText={t('confirmationDialog.unsavedData.dismiss', 'Abbrechen')}
          confirmText={t('confirmationDialog.unsavedData.confirm', 'Verwerfen')}
        />
      </Modal>
    </Modal>
  );
};

PersonalizationModal.propTypes = {
  giftCardId: or([PropTypes.string, explicitNull()]).isRequired,
  personalization: or([
    PropTypes.shape({
      to: PropTypes.string,
      message: PropTypes.string,
      from: PropTypes.string,
    }),
    explicitNull(),
  ]),
  onClose: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
};

PersonalizationModal.defaultProps = {
  personalization: null,
};

export default PersonalizationModal;
